import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, EmptyState, LoadingState, MobileSearchField, PlayerSearchInput, SectionHeader, StatusBadge, Typography } from '../../common';
import { leaguePlayerNameClass } from '../../leagues/LeagueMatchViews';
import {
  fetchClubLeagueLink,
  fetchClubMembersForLinking,
  fetchLeagueClubRoster,
  isWeakLeagueNameMatch,
  linkLeaguePlayer,
  requestClubLeagueLink,
  searchLeagueClubs,
  unlinkLeaguePlayer,
} from '../../../services/leagueService';

const SQUAD_PREVIEW_COUNT = 15;

const SQUAD_FILTERS = [
  { id: 'unlinked', label: 'Needs linking' },
  { id: 'linked', label: 'Linked' },
  { id: 'all', label: 'All' },
];

function ClubLeagueLinkRequest({ clubId, pending, onLinked }) {
  const [clubQuery, setClubQuery] = useState('');
  const [clubHits, setClubHits] = useState([]);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const q = clubQuery.trim();
    if (q.length < 2) {
      setClubHits([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchLeagueClubs(q)
        .then((rows) => {
          if (!cancelled) setClubHits(rows);
        })
        .catch(() => {
          if (!cancelled) setClubHits([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [clubQuery]);

  const handleRequest = async (leagueClubId) => {
    setRequesting(true);
    setError(null);
    try {
      await requestClubLeagueLink(clubId, leagueClubId);
      setClubQuery('');
      setClubHits([]);
      await onLinked();
    } catch (err) {
      setError(err.message);
    } finally {
      setRequesting(false);
    }
  };

  if (pending) {
    return (
      <Card size="small">
        <StatusBadge variant="warning" size="small">Pending approval</StatusBadge>
        <Typography variant="body" className="mt-3">
          Your county club link is waiting for a site admin to approve it. Once it is approved, you can match Middlesex names to club members here.
        </Typography>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader title="Link county club" subtitle="Search Middlesex clubs, then wait for site admin approval" />
      {error ? <Typography variant="warning">{error}</Typography> : null}
      <MobileSearchField
        value={clubQuery}
        onChange={(event) => setClubQuery(event.target.value)}
        placeholder="Search Hit & Run, ISO…"
        aria-label="Search county clubs"
        showIcon
      />
      <Card size="small" className="p-0 sm:p-0">
        {clubHits.length === 0 ? (
          <div className="px-3 py-4">
            <Typography variant="muted">
              {clubQuery.trim().length < 2 ? 'Type at least two letters.' : 'No county clubs match.'}
            </Typography>
          </div>
        ) : (
          <ul>
            {clubHits.map((club) => (
              <li key={club.id} className="flex items-center justify-between gap-3 border-b border-border px-3 py-2 last:border-0">
                <Typography variant="label">{club.name}</Typography>
                <Button
                  type="button"
                  size="small"
                  variant="primary"
                  disabled={requesting}
                  onClick={() => handleRequest(club.id)}
                >
                  Request link
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export function ClubLeagueSquadSection({ clubId }) {
  const navigate = useNavigate();
  const [link, setLink] = useState(undefined);
  const [linkError, setLinkError] = useState(null);

  const reloadLink = useCallback(async () => {
    const row = await fetchClubLeagueLink(clubId);
    setLink(row);
    return row;
  }, [clubId]);

  useEffect(() => {
    if (!clubId) return undefined;
    let cancelled = false;
    reloadLink()
      .catch((err) => {
        if (!cancelled) setLinkError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [clubId, reloadLink]);

  if (link === undefined && !linkError) {
    return <LoadingState message="Loading league squad…" />;
  }

  if (linkError) {
    return <Typography variant="warning">{linkError}</Typography>;
  }

  if (link?.status !== 'approved') {
    return <ClubLeagueLinkRequest clubId={clubId} pending={link?.status === 'pending'} onLinked={reloadLink} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between rounded-xl border border-border-medium bg-surface-subtle/50 p-3">
        <Typography variant="caption" className="text-text-secondary">
          Review rubber records for any county team
        </Typography>
        <Button
          variant="outline"
          size="small"
          className="w-full sm:w-auto shrink-0"
          onClick={() => navigate(`/club/${clubId}/league-performance`)}
        >
          Player performance
        </Button>
      </div>
      <ClubLeagueSquadPanel clubId={clubId} leagueClubId={link.league_club_id} />
    </div>
  );
}

export function ClubLeagueSquadPanel({ clubId, leagueClubId }) {
  const [roster, setRoster] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkingId, setLinkingId] = useState(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('unlinked');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchLeagueClubRoster(leagueClubId),
      fetchClubMembersForLinking(clubId),
    ])
      .then(([players, clubMembers]) => {
        if (cancelled) return;
        setRoster(players);
        setMembers(clubMembers);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clubId, leagueClubId]);

  const membersById = useMemo(() => new Map(members.map((row) => [row.id, row])), [members]);
  const linkedMemberIds = useMemo(() => {
    const ids = new Map();
    for (const player of roster) {
      if (player.link?.player_id) ids.set(player.link.player_id, player.display_name);
    }
    return ids;
  }, [roster]);

  const handleLinkPlayer = async (leaguePlayerId, playerId) => {
    setLinkingId(leaguePlayerId);
    setError(null);
    try {
      await linkLeaguePlayer(clubId, leaguePlayerId, playerId);
      setRoster(await fetchLeagueClubRoster(leagueClubId));
    } catch (err) {
      setError(err.message);
    } finally {
      setLinkingId(null);
    }
  };

  const handleUnlinkPlayer = async (linkId) => {
    setError(null);
    try {
      await unlinkLeaguePlayer(linkId);
      setRoster(await fetchLeagueClubRoster(leagueClubId));
    } catch (err) {
      setError(err.message);
    }
  };

  const getMutedReason = useCallback(
    (leaguePlayer) => (member) =>
      isWeakLeagueNameMatch(leaguePlayer.display_name, member.first_name, member.last_name)
        ? 'Name may not match this league player'
        : null,
    [],
  );

  const getDisabledReason = useCallback(
    (leaguePlayer) => (member) => {
      const takenBy = linkedMemberIds.get(member.id);
      if (takenBy && takenBy !== leaguePlayer.display_name) {
        return `Already linked to ${takenBy}`;
      }
      return null;
    },
    [linkedMemberIds],
  );

  const filteredRoster = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return roster.filter((player) => {
      const linkedMember = player.link ? membersById.get(player.link.player_id) : null;
      if (filter === 'unlinked' && player.link) return false;
      if (filter === 'linked' && !player.link) return false;
      if (!needle) return true;
      const memberName = linkedMember
        ? `${linkedMember.first_name ?? ''} ${linkedMember.last_name ?? ''}`.trim().toLowerCase()
        : '';
      return player.display_name.toLowerCase().includes(needle) || memberName.includes(needle);
    });
  }, [roster, filter, query, membersById]);

  useEffect(() => {
    setShowAll(false);
  }, [filter, query, leagueClubId]);

  const visibleRoster = showAll ? filteredRoster : filteredRoster.slice(0, SQUAD_PREVIEW_COUNT);

  if (loading) {
    return <LoadingState message="Loading squad…" />;
  }

  if (!roster.length) {
    return (
      <EmptyState
        title="No squad yet"
        description="Players appear here once they have a match card this season."
      />
    );
  }

  return (
    <div className="space-y-3">
      {error ? <Typography variant="warning">{error}</Typography> : null}
      <MobileSearchField
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search league or member names…"
        aria-label="Search squad names"
        showIcon
      />
      <div className="flex flex-wrap gap-2">
        {SQUAD_FILTERS.map((item) => (
          <Button
            key={item.id}
            type="button"
            size="small"
            variant={filter === item.id ? 'primary' : 'outline'}
            aria-pressed={filter === item.id}
            onClick={() => setFilter(item.id)}
          >
            {item.label}
          </Button>
        ))}
      </div>
      {filteredRoster.length === 0 ? (
        <EmptyState
          title="No matching players"
          description={query.trim() ? `No names match “${query.trim()}”.` : 'Nobody in this list yet.'}
        />
      ) : (
        <Card size="small" className="p-0 sm:p-0">
          <ul>
            {visibleRoster.map((player) => {
              const linkedMember = player.link ? membersById.get(player.link.player_id) : null;
              return (
                <li key={player.id} className="border-b border-border px-3 py-3 last:border-0">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <Typography variant="label" className={leaguePlayerNameClass(player)}>
                        {player.display_name}
                      </Typography>
                      <Typography variant="caption">
                        {player.matches} {player.matches === 1 ? 'match' : 'matches'}
                        {linkedMember
                          ? ` · linked to ${linkedMember.first_name} ${linkedMember.last_name ?? ''}`.trim()
                          : ''}
                      </Typography>
                    </div>
                    {player.link ? (
                      <Button type="button" size="small" variant="ghost" onClick={() => handleUnlinkPlayer(player.link.id)}>
                        Unlink
                      </Button>
                    ) : (
                      <div className="w-full sm:w-72" onClick={(event) => event.stopPropagation()}>
                        <PlayerSearchInput
                          placeholder="Search club members…"
                          availablePlayers={members}
                          onSelectPlayer={(member) => handleLinkPlayer(player.id, member.id)}
                          disabled={linkingId === player.id}
                          getPlayerMutedReason={getMutedReason(player)}
                          getPlayerDisabledReason={getDisabledReason(player)}
                          mutedSectionLabel={null}
                          blockedSectionLabel="Already linked"
                        />
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
      {filteredRoster.length > SQUAD_PREVIEW_COUNT ? (
        <Button
          type="button"
          variant="ghost"
          size="small"
          className="w-full"
          aria-expanded={showAll}
          onClick={() => setShowAll((prev) => !prev)}
        >
          {showAll ? 'Show fewer' : `Show all ${filteredRoster.length} players`}
        </Button>
      ) : null}
    </div>
  );
}
